import { InviteFeedCard } from "@/components/growth/InviteFeedCard";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PostCard } from "@/components/PostCard";
import { Coins, Gamepad2, ChevronRight, Users, Smartphone, Heart } from "lucide-react";
import { isStandalone } from "@/lib/push";
import { useAuth } from "@/context/AuthContext";
import { NewPostsPill } from "@/components/NewPostsPill";
import { PostComposer } from "@/components/PostComposer";
import { PostList, usePosts } from "@/components/PostList";
import { useMissions, PointsWidget, MissionsWidget, SocialWidget, LeftNav, Suggestions } from "@/components/FeedWidgets";
import { FeedCarousel } from "@/components/FeedCarousel";
import { SupportPrompt } from "@/components/SupportPrompt";
import { SkyCelebration } from "@/components/sky/SkyCelebration";
import { QuestPopup } from "@/components/quest/QuestPopup";
import { HalloweenFeedCard } from "@/components/halloween/HalloweenFeedCard";
import { QuestResult } from "@/components/quest/QuestResult";
import { WinnerTicker } from "@/components/WinnerTicker";
import { Vis } from "@/components/UiVis";
import { useUi, byOrder } from "@/lib/uiConfig";
import { GoalCelebration, FeedAnnouncements } from "@/components/FeedExtras";
import { MugFeedBanner } from "@/components/merch/MugFeedBanner";
import { MonthGoalFeedCard } from "@/components/csupport/GoalBar";
import { NewestSupporterCard } from "@/components/NewestSupporterCard";
import { SoulmateFeedCard } from "@/components/SoulmateFeedCard";
import { DonateFeedCard } from "@/components/donate/DonateFeedCard";
import { ChallengeWidget } from "@/components/ChallengeWidget";
import { QuestFeedCard } from "@/components/quest/QuestFeedCard";
import { TasksWidget } from "@/components/TasksWidget";
import { GamesFeedCard } from "@/components/GamesFeedCard";
import { AllInFeedCard } from "@/components/games/AllInGifts";
import { MysteryBoxFeedCard, MysteryPromoFeedCard } from "@/components/MysteryBox";
import { InstallFeedPopup } from "@/components/InstallFeedPopup";
import { HomeHero } from "@/components/home/HomeHero";
import { WhatsAppFeedButton } from "@/components/whatsapp/WhatsAppFeedButton";
import { DropFeedCard } from "@/components/drops/Drops";
import { FeedRotator } from "@/components/feed/FeedRotator";
import { AdminWelcomePopup } from "@/components/admin/AdminWelcomePopup";
import { VaultFeedBanner } from "@/components/feed/VaultFeedBanner";
import { ThanksStefanBanner } from "@/components/feed/ThanksStefanBanner";
import { PushCard } from "@/components/PushCard";


const GamesButton = () => (
  <Link to="/spiele" data-testid="feed-games-button"
    className="group relative flex items-center gap-4 p-4 pt-6 rounded-3xl pink-gradient text-white shadow-lg shadow-rose-200 active:scale-[0.98] hover:-translate-y-0.5 transition-transform">
    <span className="absolute -top-3 left-4 px-3 py-1 rounded-full bg-amber-300 text-amber-950 text-[11px] font-extrabold tracking-wide shadow-md new-badge" data-testid="feed-games-new-banner">✨ Neue Spiele verfügbar</span>
    <span className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center shrink-0"><Gamepad2 className="w-6 h-6" /></span>
    <span className="flex-1 min-w-0"><span className="block font-display text-lg font-extrabold leading-tight">Zu den Spielen</span><span className="block text-xs text-white/85">Glücksrad, Rubbellose, Duelle & mehr – Punkte sammeln</span></span>
    <ChevronRight className="w-6 h-6 shrink-0 group-hover:translate-x-1 transition-transform" />
  </Link>
);

const InstallButton = () => {
  const { user } = useAuth();
  if (isStandalone() || user?.app_installed_at) return null;
  return (
    <Link to="/app" onClick={() => api.post("/app/install-click").catch(() => {})} data-testid="feed-install-button"
      className="group relative flex items-center gap-4 p-4 sm:p-5 rounded-3xl bg-gradient-to-br from-[#ff7aa8] via-[#f43f7e] to-[#b3164f] text-white shadow-lg shadow-rose-300/60 vip-sheen active:scale-[0.98] hover:-translate-y-0.5 transition-transform overflow-hidden">
      <span className="absolute -right-6 -bottom-8 w-32 h-32 rounded-full bg-white/10 pointer-events-none" />
      <span className="absolute right-16 -top-10 w-20 h-20 rounded-full bg-white/10 pointer-events-none" />
      <span className="relative w-14 h-14 rounded-2xl bg-white/20 ring-1 ring-white/40 backdrop-blur-sm flex items-center justify-center shrink-0 install-phone">
        <Smartphone className="w-7 h-7 text-white" strokeWidth={2.2} />
        <Heart className="absolute w-3 h-3 text-white top-[1.15rem]" fill="currentColor" />
      </span>
      <span className="relative flex-1 min-w-0">
        <span className="block font-display text-lg sm:text-xl font-extrabold leading-tight">App jetzt installieren</span>
        <span className="block text-xs sm:text-sm text-white/85 mt-0.5">Schneller öffnen, Push-Nachrichten & Bonus</span>
        <span className="mt-1.5 inline-flex items-center gap-1 px-2.5 h-6 rounded-full bg-gradient-to-r from-amber-200 to-amber-400 text-[11px] font-extrabold text-[#4a2a08] shadow-sm" data-testid="feed-install-bonus"><Coins className="w-3.5 h-3.5" /> +50 Punkte</span>
      </span>
      <span className="relative shrink-0 w-10 h-10 rounded-full bg-white text-rose-600 flex items-center justify-center shadow-md group-hover:translate-x-0.5 transition-transform"><ChevronRight className="w-5 h-5" strokeWidth={3} /></span>
    </Link>
  );
};

const SupportButton = () => {
  const [g, setG] = useState(null);
  useEffect(() => { api.get("/supporters/campaign").then(({ data }) => setG(data)).catch(() => {}); }, []);
  const n = g?.count || 0;
  const goal = g?.enabled && g.until >= new Date().toISOString().slice(0, 10) ? g : null;
  const pct = goal ? Math.min(100, Math.round((n / goal.target) * 100)) : 0;
  return (
  <Link to="/community-unterstuetzen" data-testid="feed-support-button"
    className="group relative flex items-center gap-4 p-4 rounded-3xl vip-gold-bg vip-sheen border border-amber-400/60 shadow-lg shadow-amber-200/60 active:scale-[0.98] hover:-translate-y-0.5 transition-transform overflow-hidden">
    <span className="w-12 h-12 rounded-2xl bg-gradient-to-br from-pink-200 via-rose-300 to-amber-200 flex items-center justify-center shrink-0 shadow-md"><Heart className="w-6 h-6 text-rose-700" fill="currentColor" /></span>
    <span className="flex-1 min-w-0">
      <span className="block font-display text-lg font-extrabold leading-tight vip-gold-text">Projekt unterstützen</span>
      <span className="block text-xs text-[#f6e3a1]/90">und exklusive Vorteile freischalten</span>
      {n > 0 && !goal && <span className="mt-1.5 inline-flex items-center gap-1.5 px-2.5 h-6 rounded-full bg-amber-300/15 ring-1 ring-amber-300/40 text-[11px] font-bold text-amber-200" data-testid="feed-support-count"><Users className="w-3.5 h-3.5" /> {n.toLocaleString("de-DE")} {n === 1 ? "Mitglied unterstützt" : "Mitglieder unterstützen"} schon</span>}
      {goal && (
        <span className="block mt-2" data-testid="feed-support-goal">
          <span className="flex items-center justify-between gap-2 text-[11px] font-bold text-amber-200">
            <span className="inline-flex items-center gap-1 min-w-0 leading-tight" data-testid="feed-support-count"><Users className="w-3.5 h-3.5 shrink-0" /> {n.toLocaleString("de-DE")}/{goal.target.toLocaleString("de-DE")} Unterstützer {goal.label}</span>
            <span className="tabular-nums shrink-0">{pct >= 100 ? "Geschafft! 🎉" : `${pct} %`}</span>
          </span>
          <span className="mt-1 block h-2 rounded-full bg-black/30 ring-1 ring-amber-300/30 overflow-hidden">
            <span className="block h-full rounded-full bg-gradient-to-r from-amber-200 via-amber-400 to-amber-600 shadow-[0_0_10px_rgba(246,201,69,.8)] transition-[width] duration-1000" style={{ width: `${Math.max(pct, 3)}%` }} data-testid="feed-support-goal-bar" />
          </span>
        </span>
      )}
    </span>
    <ChevronRight className="w-6 h-6 shrink-0 text-amber-300 group-hover:translate-x-1 transition-transform" />
  </Link>
  );
};

export default function Feed() {
  const feed = usePosts("/feed");
  const [feedLoadedAt] = useState(() => new Date().toISOString());
  const [pin, setPin] = useState(null);
  const [pollPin, setPollPin] = useState(null);
  useEffect(() => {
    const loadPin = () => api.get("/feed/pinned").then(({ data }) => setPin(data)).catch(() => {});
    window.addEventListener("feed-pin-changed", loadPin);
    return () => window.removeEventListener("feed-pin-changed", loadPin);
  }, []);
  useEffect(() => {
    api.get("/feed/pinned").then(({ data }) => setPin(data)).catch(() => {});
    api.get("/feed/pinned-poll").then(({ data }) => setPollPin(data)).catch(() => {});
  }, []);
  const pinnedIds = [pin?.id, pollPin?.id].filter(Boolean);
  const list = { ...feed, posts: feed.posts ? feed.posts.filter((p) => !pinnedIds.includes(p.id)) : feed.posts };
  const onPosted = (p) => (p.poll_pinned ? setPollPin(p) : feed.prepend(p));
  const missions = useMissions();
  const ui = useUi();
  const sideCards = byOrder(ui, [["card-tasks", <TasksWidget />], ["card-gamescard", <GamesFeedCard />], ["card-challenge", <ChallengeWidget />]])
    .map(([id, el]) => <Vis key={id} id={id}>{el}</Vis>);
  return (
    <div className="grid lg:grid-cols-[1fr_20rem] min-[1360px]:grid-cols-[14rem_1fr_20rem] gap-6 items-start" data-testid="feed-page">
    <LeftNav />
    <div className="space-y-4 min-w-0">
      <WinnerTicker />
      <HomeHero />
      <InviteFeedCard />
      <GamesButton />
      <FeedRotator>
        <Vis key="missions" id="missions"><MissionsWidget m={missions.data} reload={missions.load} /></Vis>
        <HalloweenFeedCard key="halloween" />
        <ThanksStefanBanner key="thanks-stefan" />
        <MugFeedBanner key="mug" />
        <SupportButton key="support" />
        <WhatsAppFeedButton key="whatsapp" />
        <Vis key="quest" id="card-quest"><QuestFeedCard /></Vis>
        <InstallButton key="install" />
        <VaultFeedBanner key="vault" />
        <PushCard key="push" />
        <Vis key="tasks" id="card-tasks"><TasksWidget /></Vis>
        <DropFeedCard key="drop" />
        <Vis key="donate" id="card-donate"><DonateFeedCard /></Vis>
        <Vis key="announce" id="card-announce"><FeedAnnouncements /></Vis>
        <MonthGoalFeedCard key="monthgoal" />
        <Vis key="challenge" id="card-challenge"><ChallengeWidget /></Vis>
        <Vis key="allin" id="card-allin"><AllInFeedCard /></Vis>
        <Vis key="newest" id="card-supporter"><NewestSupporterCard /></Vis>
        <Vis key="mystery" id="card-mystery"><MysteryBoxFeedCard /></Vis>
        <Vis key="gamescard" id="card-gamescard"><GamesFeedCard /></Vis>
        <Vis key="secret" id="card-secret"><SoulmateFeedCard /></Vis>
        <Vis key="mystery-promo" id="card-mystery"><MysteryPromoFeedCard /></Vis>
        <Vis key="goal" id="card-goal"><GoalCelebration /></Vis>
      </FeedRotator>
      <InstallFeedPopup />
      <AdminWelcomePopup />
      <QuestPopup />
      <QuestResult />
      <SupportPrompt />
      <SkyCelebration />
      {pin && <PostCard key={pin.id} post={pin} pinned onRemoved={() => setPin(null)} onUnpinned={(p) => { setPin(null); if (!feed.posts?.some((x) => x.id === p.id)) feed.prepend(p); window.dispatchEvent(new CustomEvent("feed-pin-changed")); }} />}
      {pollPin && <PostCard key={pollPin.id} post={pollPin} pinned onRemoved={() => setPollPin(null)} onUnpinned={(p) => { setPollPin(null); if (!feed.posts?.some((x) => x.id === p.id)) feed.prepend(p); }} />}
      <PostComposer onPosted={onPosted} />
      <NewPostsPill since={feed.posts ? (feed.posts[0]?.created_at || feedLoadedAt) : null} onShow={feed.reload} anchorId="feed-post-list" />
      <div id="feed-post-list" />
      <PostList feed={list} empty="Noch keine Beiträge – teile als Erste/r etwas mit der Community! ✨" />
    </div>
    <aside className="hidden lg:block space-y-4 sticky top-24" data-testid="feed-sidebar">
      <PointsWidget m={missions.data} />
      <Vis id="missions"><MissionsWidget m={missions.data} reload={missions.load} /></Vis>
      {sideCards}
      <SocialWidget />
      <Suggestions />
    </aside>
    </div>
  );
}
